// WLP Adoption hook — fetch eligibility + emit events.
//
// Resilient by design: if /eligibility returns 404 (feature flag OFF
// in this environment), the hook simply reports `disabled: true` and
// caller components render nothing. No retries, no toasts.
//
// Admin Demo / impersonation guard (Feb 2026 P1 UX fix):
// The D1 Demo Preview middleware blocks every non-sandbox write
// (POST/PUT/PATCH/DELETE) with HTTP 412 `errors.demo_readonly_mode`.
// /wlp-adoption/event, /wlp-adoption/snooze AND the wishlist save
// endpoint are all blocked under demo, which caused a UX dead-end:
// the modal would fire, dismiss/save would silently 412, and on
// next navigation the eligibility re-fetch returned `eligible`
// again → infinite loop. Fix: when a demo token is active, treat
// the feature as disabled so the modal never mounts. The admin
// running impersonation has no business completing the demo
// user's wishlist anyway.

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

const EMPTY = {
  loading: true,
  disabled: false,
  showModal: false,
  showBanner: false,
  reason: "unknown",
};

// Read the demo-token flag synchronously so we can short-circuit
// before any network round-trip. The lib/api.js axios interceptor
// uses the exact same localStorage key for its impersonation
// detection (kept in sync intentionally — single source of truth).
const isDemoSessionActive = () => {
  try {
    return Boolean(localStorage.getItem("tsw_demo_token"));
  } catch {
    return false;
  }
};

export default function useWlpAdoption() {
  const { user } = useAuth();
  const [state, setState] = useState(EMPTY);
  const shownEmittedRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setState({ ...EMPTY, loading: false, reason: "no_user" });
      return;
    }
    if (isDemoSessionActive()) {
      // Demo Preview: every WLP write would 412. Never mount.
      setState({ ...EMPTY, loading: false, disabled: true, reason: "demo_session_active" });
      return;
    }
    try {
      const r = await api.get("/wlp-adoption/eligibility");
      setState({
        loading: false,
        disabled: false,
        showModal: !!r.data.show_modal,
        showBanner: !!r.data.show_banner,
        reason: r.data.reason || "unknown",
      });
    } catch (err) {
      if (err?.response?.status === 404) {
        setState({ ...EMPTY, loading: false, disabled: true, reason: "feature_disabled" });
      } else {
        setState({ ...EMPTY, loading: false, reason: "error" });
      }
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const emit = useCallback(async (event) => {
    // Fire-and-forget event tracking. We deliberately do not block UI
    // on network round-trips.
    if (state.disabled) return;
    if (event === "shown" && shownEmittedRef.current) return;
    if (event === "shown") shownEmittedRef.current = true;
    try {
      await api.post("/wlp-adoption/event", { event });
    } catch { /* swallow — tracking is best-effort */ }
  }, [state.disabled]);

  const snooze = useCallback(async () => {
    if (state.disabled) return;
    // Close the modal LOCALLY first, regardless of network outcome.
    // The server-side snooze (POST /wlp-adoption/snooze) is the
    // authoritative source of `snoozed_until`, but if the POST fails
    // (412 readonly demo, transient network error, etc.) we must
    // NOT leave the modal stuck open — that was the root cause of
    // the Feb 2026 P1 loop. Worst case for the user: the modal
    // reappears on next session refresh (capped by the server-side
    // PROMPT_RATE_LIMIT_DAYS soft gate).
    setState((s) => ({ ...s, showModal: false }));
    try {
      await api.post("/wlp-adoption/snooze");
    } catch { /* swallow — local close already applied */ }
  }, [state.disabled]);

  return { ...state, refresh, emit, snooze };
}
